import {
  Badge,
  Box,
  Button,
  Container,
  ExpandableSection,
  FormField,
  Grid,
  Select,
  SelectProps,
  SpaceBetween,
  TextContent,
} from '@amzn/awsui-components-core';
import {
  PublicationFeedback,
  PublicationFeedbackActivity,
  PublicationFeedbackActivityType,
  PublicationFeedbackState,
} from '@amzn/awscat-pattern-library-service-model';
import { TableHeader } from '../common/CommonComponents';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  getUpdateRequestTitle,
  publicationFeedbackTypeDisplayName,
  publicationFeedbackStateDisplayName,
  PublicationFeedbackTypeSeverity,
  isFeedbackClosed,
} from '../utils/DisplayNames';
import { AppContext } from '../../contexts/AppContext';
import { ContentViewerContext } from '../workflow-viewer/ContentViewerContext';
import BrowserHistory from '../utils/BrowserHistory';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { getUserRoles } from '../../api/UtilsApi';
import { getText } from '@amzn/awscat-editor';
import SimpleRichTextInput from '../common/SimpleRichTextInput';
import SimpleRichTextOutput from '../common/SimpleRichTextOutput';
import InputSizeMessage from '../utils/InputSizeMessage';
import CharacterCountMessage from '../utils/CharacterCountMessage';

/** Minimum character length for a valid feedback comment. */
const COMMENT_MIN_LENGTH = 5;
/** Maximum character length for a valid feedback comment. */
const COMMENT_MAX_LENGTH = 500;

/** Dropdown options for the feedback status selector, shown to content authors. */
const STATUS_OPTIONS: SelectProps.Option[] = [
  { value: PublicationFeedbackState.CREATED, label: 'Assigned' },
  { value: PublicationFeedbackState.PENDING, label: 'In progress' },
  { value: PublicationFeedbackState.RESOLVED, label: 'Resolved' },
  { value: PublicationFeedbackState.NO_CHANGE_NEEDED, label: 'No change needed' },
];

/** Maps severity labels to Cloudscape Badge color tokens. */
const SEVERITY_BADGE_COLORS: Record<string, 'severity-high' | 'severity-medium' | 'severity-low'> =
  {
    High: 'severity-high',
    Medium: 'severity-medium',
    Low: 'severity-low',
  };

/**
 * Payload emitted via {@link PublicationFeedbackListItemProps.onUpdate} after any feedback update.
 *
 * Fired for both status changes and comment-only updates. The optional `activity` field
 * propagates the newly created activity back to the parent (`UpdateRequestsPage`) so it can
 * patch the feedback's `activities` array in its own state. Without this, the parent's copy
 * of `activities` becomes stale — if the component later remounts (e.g., toggling
 * "Show closed requests" or list reordering), `localActivities` reinitializes from the
 * parent's stale props and the new activity is lost.
 */
export interface FeedbackUpdate {
  feedbackId: string;
  newState?: PublicationFeedbackState;
  activity?: PublicationFeedbackActivity;
}

interface PublicationFeedbackListItemProps {
  /** The content ID this feedback belongs to, used for API calls. */
  readonly contentId: string;
  /** User ID of the content owner. Used to determine if the current user can change status. */
  readonly ownerId?: string;
  /** The feedback item to render. */
  readonly publicationFeedback: PublicationFeedback;
  /** Called when the feedback state changes (status update or reopen). */
  readonly onUpdate?: (update: FeedbackUpdate) => void;
  /** When true (or when the URL hash matches), the item scrolls into view with a highlight pulse. */
  readonly highlight?: boolean;
}

/** Returns true if the given state represents a terminal/closed feedback state. */
const isClosingState = (s: PublicationFeedbackState): boolean =>
  s === PublicationFeedbackState.RESOLVED || s === PublicationFeedbackState.NO_CHANGE_NEEDED;

/** Returns an error message if the comment text is invalid, or an empty string if valid. */
const validateComment = (text: string): string => {
  if (text.length < COMMENT_MIN_LENGTH) return `${COMMENT_MIN_LENGTH} characters required`;
  if (text.length > COMMENT_MAX_LENGTH) return `Maximum ${COMMENT_MAX_LENGTH} characters allowed`;
  return '';
};

/** Extracts plain text length from a comment that may be HTML or plain text. */
const getPlainTextLength = (comment: string): number => {
  if (!comment) return 0;
  return getText(comment).length;
};

/** Maps a {@link PublicationFeedbackTypeSeverity} to a Cloudscape Badge color token. */
const getSeverityBadgeColor = (
  severity: PublicationFeedbackTypeSeverity,
): 'severity-high' | 'severity-medium' | 'severity-low' | undefined =>
  SEVERITY_BADGE_COLORS[severity];

/** Renders a single activity entry (status change and/or comment) in the feedback timeline. */
const ActivityItem = ({ activity }: { activity: PublicationFeedbackActivity }) => {
  const userName = activity.userProfile?.name ?? 'Unknown';
  const date = activity.createdAt?.toISODate() ?? '';

  return (
    <div className="feedback-activity-item">
      <Box color="text-body-secondary" fontSize="body-s">
        {userName} &nbsp; {date}
      </Box>
      {activity.activityType === PublicationFeedbackActivityType.STATUS_CHANGE &&
        activity.newState && (
          <Box>
            <Box fontWeight="bold" display="inline">
              Status:
            </Box>{' '}
            {publicationFeedbackStateDisplayName.get(activity.newState)?.name ?? activity.newState}
          </Box>
        )}
      {activity.comment && (
        <Box>
          <Box fontWeight="bold" display="inline">
            Comment:
          </Box>{' '}
          <SimpleRichTextOutput value={activity.comment} />
        </Box>
      )}
    </div>
  );
};

/**
 * Renders a single publication feedback item with its activity log and action controls.
 *
 * Behaviour varies by role:
 * - Authors (content owner or co-author) can change the feedback status and add comments.
 *   A comment is required when closing feedback (Resolved / No change needed).
 * - Non-authors can only add comments to open feedback, or reopen closed feedback.
 *
 * The component supports deep-linking: navigating to `#feedback-{id}` scrolls the item
 * into view with a highlight pulse animation (see `publication-feedback.scss`).
 */
const PublicationFeedbackListItem = (props: PublicationFeedbackListItemProps) => {
  const {
    publicationFeedbackData: feedbackData,
    feedbackId,
    userProfile,
    createdAt,
    state,
    activities,
  } = props.publicationFeedback;
  const appContext = useContext(AppContext);
  const { coAuthors } = useContext(ContentViewerContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [currentState, setCurrentState] = useState<PublicationFeedbackState>(state);
  const [isUpdating, setIsUpdating] = useState(false);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [localActivities, setLocalActivities] = useState<PublicationFeedbackActivity[]>(
    activities ?? [],
  );
  const [richTextCharCount, setRichTextCharCount] = useState(0);

  const mplService = useMemo(() => MplServiceClientFactory.create(), []);

  // Sync localActivities when the parent's activities prop changes.
  // This covers the case where the component re-renders (without remounting) after
  // handleFeedbackUpdate in UpdateRequestsPage patches the feedback's activities array.
  // Without this, localActivities would only reflect what was appended locally and could
  // drift from the canonical state held by the parent.
  useEffect(() => {
    if (activities) {
      setLocalActivities(activities);
    }
  }, [activities]);

  useEffect(() => {
    const checkAndScroll = () => {
      const shouldHighlight = props.highlight || window.location.hash === `#feedback-${feedbackId}`;
      setIsHighlighted(shouldHighlight);
      if (shouldHighlight && containerRef.current) {
        containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    checkAndScroll();
    const unlisten = BrowserHistory.listen(() => checkAndScroll());
    return () => unlisten();
  }, [feedbackId, props.highlight]);

  const isFeedbackResolved = isFeedbackClosed(currentState);
  const currentUserProfile = appContext.authnInfo?.userProfile;

  /** Checks whether the current user is the content owner or a co-author. */
  const isAuthor = (): boolean =>
    [props?.ownerId, ...(coAuthors || []).map((ca) => ca.userId)].includes(
      currentUserProfile?.userId,
    );

  /** Checks whether the current user has a privileged role (admin, tech writer) or is a bar raiser. */
  const hasStatusChangePermission = (): boolean => {
    if (!currentUserProfile) return false;
    const { isAdmin, isTechWriter, isBarRaiser } = getUserRoles(currentUserProfile);
    return isAdmin || isTechWriter || isBarRaiser;
  };

  const canAuthorChangeStatus = (isAuthor() || hasStatusChangePermission()) && !isFeedbackResolved;
  const isStatusChange = selectedStatus !== '' && selectedStatus !== currentState;
  const isCommentRequired =
    (canAuthorChangeStatus &&
      isClosingState(selectedStatus as PublicationFeedbackState) &&
      isStatusChange) ||
    isFeedbackResolved;

  /** Shared logic for all feedback update operations. */
  const submitFeedbackUpdate = async (config: {
    state?: PublicationFeedbackState;
    comment?: string;
  }) => {
    const previousState = currentState;
    if (config.state) {
      setCurrentState(config.state);
      setSelectedStatus('');
    }
    setIsUpdating(true);
    setCommentError('');

    try {
      const response = await mplService.updatePublicationFeedback(
        props.contentId,
        feedbackId,
        config,
      );

      if (response?.activity) {
        setLocalActivities((prev) => [...prev, response.activity]);
      }
      setComment('');
      setRichTextCharCount(0);

      // Always notify the parent when an activity is created, not just on status changes.
      // Comment-only updates also produce activities that need to be propagated to the
      // parent's state. Otherwise, if the component remounts (e.g., toggling "Show closed
      // requests" or list reordering), localActivities reinitializes from stale props.
      if (props.onUpdate) {
        props.onUpdate({
          feedbackId,
          newState: config.state,
          activity: response?.activity,
        });
      }
    } catch (error) {
      if (config.state) {
        setCurrentState(previousState);
        setSelectedStatus('');
      }
      console.error('Failed to update feedback:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  /** Validates the comment field, sets error state, and returns true if valid. */
  const requireValidComment = (): boolean => {
    const plainLength = getPlainTextLength(comment);
    if (!plainLength) {
      setCommentError(`${COMMENT_MIN_LENGTH} characters required`);
      return false;
    }
    const error = validateComment(getText(comment));
    if (error) {
      setCommentError(error);
      return false;
    }
    return true;
  };

  /** Handles the primary action for open feedback — status change and/or comment. */
  const handleSaveChanges = async () => {
    const hasCommentContent = richTextCharCount > 0;

    // Comment is mandatory when author is closing
    if (isCommentRequired && !requireValidComment()) return;

    // Validate if there's a comment
    if (hasCommentContent && !requireValidComment()) return;

    if (!isStatusChange && !hasCommentContent) return;

    const config: { state?: PublicationFeedbackState; comment?: string } = {};
    if (isStatusChange) config.state = selectedStatus as PublicationFeedbackState;
    if (hasCommentContent) config.comment = comment;

    await submitFeedbackUpdate(config);
  };

  /** Reopens closed feedback by resetting state to CREATED. Requires a comment. */
  const handleReopen = async () => {
    if (!requireValidComment()) return;
    await submitFeedbackUpdate({ state: PublicationFeedbackState.CREATED, comment });
  };

  /** Adds a standalone comment without changing feedback state (non-author action). */
  const handleAddComment = async () => {
    if (!requireValidComment()) return;
    await submitFeedbackUpdate({ comment });
  };

  const feedbackTypeInfo = feedbackData?.issueType
    ? publicationFeedbackTypeDisplayName.get(feedbackData.issueType)
    : undefined;
  const severity = feedbackTypeInfo?.severity;

  const currentStateLabel =
    publicationFeedbackStateDisplayName.get(currentState)?.name ?? 'Unknown';
  const statusOptions: SelectProps.Option[] = STATUS_OPTIONS.filter(
    (opt) => opt.value !== currentState,
  );

  const getSelectedOption = (): SelectProps.Option | null =>
    statusOptions.find((opt) => opt.value === selectedStatus) ?? null;

  const sortedActivities = [...localActivities].sort((a, b) => {
    const timeA = a.createdAt?.toMillis() ?? 0;
    const timeB = b.createdAt?.toMillis() ?? 0;
    return timeA - timeB;
  });

  const buttonAction = canAuthorChangeStatus ? handleSaveChanges : handleAddComment;
  const buttonLabel = canAuthorChangeStatus ? 'Save changes' : 'Add comment';
  const commentPlainLength = richTextCharCount;
  const hasComment = commentPlainLength > 0;
  const isCommentLengthValid =
    commentPlainLength >= COMMENT_MIN_LENGTH && commentPlainLength <= COMMENT_MAX_LENGTH;

  // Button is disabled when:
  // - An update is in progress
  // - Author mode: no status change and no comment (nothing to do),
  //   OR comment is required (closing state) but missing/invalid,
  //   OR comment is present but fails length validation
  // - Non-author mode: no comment or comment fails length validation
  const buttonDisabled = (() => {
    if (isUpdating) return true;
    if (canAuthorChangeStatus) {
      if (!isStatusChange && !hasComment) return true; // nothing to do
      if (isCommentRequired && !isCommentLengthValid) return true; // closing requires valid comment
      if (hasComment && !isCommentLengthValid) return true; // comment present but invalid
      return false;
    }
    return !hasComment || !isCommentLengthValid;
  })();

  const commentLabel = (
    <span className="feedback-comment-label">
      <Box fontWeight="bold">Comment</Box>
      {isCommentRequired && (
        <span className="feedback-required-indicator" aria-label="Required">
          <Box color="text-status-error" fontSize="body-s" fontWeight="bold">
            ⚠ Required
          </Box>
        </span>
      )}
    </span>
  );

  const commentSection = (
    <div className="feedback-comment-section">
      <FormField
        label={commentLabel}
        errorText={commentError}
        stretch={true}
        i18nStrings={{ errorIconAriaLabel: 'Error' }}
      >
        <SimpleRichTextInput
          value={comment}
          onChange={(html) => {
            setComment(html);
            if (commentError) setCommentError('');
          }}
          onCharacterCountChange={setRichTextCharCount}
          disabled={isUpdating}
        />
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          <InputSizeMessage
            input={'x'.repeat(commentPlainLength)}
            minNum={COMMENT_MIN_LENGTH}
            maxNum={COMMENT_MAX_LENGTH}
          />
          <CharacterCountMessage input={'x'.repeat(commentPlainLength)} />
        </Grid>
      </FormField>
    </div>
  );

  return (
    <div
      ref={containerRef}
      id={`feedback-${feedbackId}`}
      className={isHighlighted ? 'feedback-highlight' : undefined}
    >
      <Container
        header={
          <TableHeader
            title={getUpdateRequestTitle(feedbackData)}
            description={`Submitted by ${userProfile?.name} on ${createdAt?.toISODate()}`}
            info={
              <span className="feedback-header-info">
                {severity && severity !== 'NA' && (
                  <span className="badge-align">
                    <Badge color={getSeverityBadgeColor(severity)}>{severity}</Badge>
                  </span>
                )}{' '}
                <span className="badge-align">
                  <Badge color="grey">{isFeedbackResolved ? 'Closed' : 'Open'}</Badge>{' '}
                </span>
              </span>
            }
            actionButtons={
              currentState && (
                <Box>
                  <Box fontWeight="bold" display="inline">
                    Status:
                  </Box>{' '}
                  {publicationFeedbackStateDisplayName.get(currentState)?.name}
                </Box>
              )
            }
          />
        }
      >
        <SpaceBetween size="m">
          <TextContent>
            {feedbackData?.improvement && <SimpleRichTextOutput value={feedbackData.improvement} />}
            {feedbackData?.intendedAction && (
              <SimpleRichTextOutput value={feedbackData.intendedAction} />
            )}
          </TextContent>

          {sortedActivities.length > 0 && (
            <ExpandableSection headerText={`Activity (${sortedActivities.length})`} defaultExpanded>
              <SpaceBetween size="xs">
                {sortedActivities.map((activity) => (
                  <ActivityItem key={activity.activityId} activity={activity} />
                ))}
              </SpaceBetween>
            </ExpandableSection>
          )}

          {canAuthorChangeStatus && (
            <Box>
              <SpaceBetween size="xs" direction="vertical">
                <Box fontWeight="bold">Status</Box>
                <Select
                  selectedOption={getSelectedOption()}
                  onChange={({ detail }) => {
                    setSelectedStatus(detail.selectedOption.value ?? '');
                    setCommentError('');
                  }}
                  options={statusOptions}
                  placeholder={`${currentStateLabel}`}
                  disabled={isUpdating}
                  ariaRequired={false}
                />
              </SpaceBetween>
            </Box>
          )}

          {!isFeedbackResolved && (
            <SpaceBetween size="xs" direction="vertical">
              {commentSection}
              <Box float="right">
                <Button
                  variant="primary"
                  onClick={buttonAction}
                  loading={isUpdating}
                  disabled={buttonDisabled}
                >
                  {buttonLabel}
                </Button>
              </Box>
            </SpaceBetween>
          )}

          {isFeedbackResolved && (
            <SpaceBetween size="xs" direction="vertical">
              {commentSection}
              <Box float="right">
                <Button
                  variant="primary"
                  onClick={handleReopen}
                  loading={isUpdating}
                  disabled={isUpdating || !hasComment || !isCommentLengthValid}
                >
                  Reopen
                </Button>
              </Box>
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Container>
    </div>
  );
};

export default PublicationFeedbackListItem;
